import { z } from "zod";

/**
 * GoBD-conform Archive Configuration
 *
 * GoBD (Grundsätze zur ordnungsmäßigen Führung und Aufbewahrung von Büchern,
 * Aufzeichnungen und Unterlagen in elektronischer Form sowie zum Datenzugriff)
 *
 * Key requirements:
 * - 10-year retention period for business documents
 * - Immutability (WORM - Write Once Read Many)
 * - Complete audit trail
 * - Verifiable integrity (checksums)
 * - Structured access with proper authorization
 */
export const ArchiveConfigSchema = z.object({
  enabled: z.boolean().default(true),

  // Retention settings
  retentionYears: z.number().int().min(1).max(30).default(10), // GoBD requires 10 years
  retentionPolicy: z.enum(["GOBD", "GDPR", "CUSTOM"]).default("GOBD"),

  // Document types to archive
  archiveInvoices: z.boolean().default(true),
  archiveCreditNotes: z.boolean().default(true),
  archiveDebitNotes: z.boolean().default(true),
  archiveDunningLetters: z.boolean().default(true),
  archiveEmails: z.boolean().default(true), // Store full email correspondence
  archiveAttachments: z.boolean().default(true),
  archiveAuditLogs: z.boolean().default(true),

  // Integrity verification
  enableChecksums: z.boolean().default(true),
  checksumAlgorithm: z.enum(["SHA-256", "SHA-512"]).default("SHA-256"),
  periodicIntegrityCheck: z.boolean().default(true),
  integrityCheckIntervalDays: z.number().int().min(1).max(365).default(30),
  lastIntegrityCheckAt: z.string().datetime().optional(),
  lastIntegrityCheckStatus: z.enum(["PASSED", "FAILED", "PENDING"]).optional(),

  // Storage settings
  storageLocation: z.enum(["R2_EU", "R2_US", "R2_ASIA"]).default("R2_EU"), // GDPR compliance
  encryptAtRest: z.boolean().default(true),
  compressArchives: z.boolean().default(true),

  // Legal hold
  legalHoldEnabled: z.boolean().default(false),
  legalHoldReason: z.string().optional(),
  legalHoldStartDate: z.string().datetime().optional(),
  legalHoldEndDate: z.string().datetime().optional(),

  // Export settings
  allowExport: z.boolean().default(true),
  exportFormats: z.array(z.enum(["PDF", "XML", "JSON", "CSV", "DATEV"])).default(["PDF", "XML", "JSON"]),

  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});

export type ArchiveConfig = z.infer<typeof ArchiveConfigSchema>;

/**
 * Auditor Mode Configuration
 * Provides secure, read-only access for external auditors (tax authorities, accountants)
 */
export const AuditorConfigSchema = z.object({
  enabled: z.boolean().default(false),

  // Access control
  auditorAccessEnabled: z.boolean().default(false),
  auditorAccessExpiry: z.string().datetime().optional(), // Temporary access window

  // What auditors can access
  accessScopes: z.array(z.enum([
    "INVOICES",
    "CREDIT_NOTES",
    "DEBIT_NOTES",
    "DUNNING",
    "AUDIT_LOGS",
    "DOCUMENTS",
    "PARTNERS",
    "FINANCIAL_REPORTS",
    "EMAIL_RECORDS",
  ])).default(["INVOICES", "CREDIT_NOTES", "DEBIT_NOTES", "DUNNING", "AUDIT_LOGS"]),

  // Time range restriction
  accessStartDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(), // Limit to specific fiscal years
  accessEndDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),

  // Security
  requireMfa: z.boolean().default(true),
  allowedIpAddresses: z.array(z.string()).default([]), // IP whitelist
  sessionTimeoutMinutes: z.number().int().min(5).max(480).default(60),
  maxConcurrentSessions: z.number().int().min(1).max(5).default(1),

  // Audit of auditor
  logAllAccess: z.boolean().default(true),
  notifyOnAccess: z.boolean().default(true), // Notify admin when auditor logs in
  notifyEmail: z.string().email().optional(),

  // Export restrictions for auditors
  allowAuditorExport: z.boolean().default(true),
  auditorExportFormats: z.array(z.enum(["PDF", "XML", "JSON", "CSV", "DATEV"])).default(["PDF"]),
  watermarkExports: z.boolean().default(true), // Add audit watermark to exports
});

export type AuditorConfig = z.infer<typeof AuditorConfigSchema>;

/**
 * Auditor Access Token - For generating temporary auditor access
 */
export const AuditorAccessTokenSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),

  // Token details
  token: z.string().min(32), // Secure random token
  tokenHash: z.string(), // Stored hash for verification

  // Auditor info
  auditorName: z.string().trim().min(1),
  auditorEmail: z.string().email(),
  auditorOrganization: z.string().trim().optional(), // e.g., "Finanzamt München"
  auditorReference: z.string().trim().optional(), // External reference number

  // Validity
  validFrom: z.string().datetime(),
  validUntil: z.string().datetime(),
  isActive: z.boolean().default(true),
  revokedAt: z.string().datetime().optional(),
  revokedBy: z.string().uuid().optional(),
  revokeReason: z.string().optional(),

  // Scope
  accessScopes: z.array(z.string()).default(["INVOICES", "CREDIT_NOTES", "DEBIT_NOTES", "AUDIT_LOGS"]),
  fiscalYears: z.array(z.number().int()).optional(), // Specific years auditor can access

  // Usage tracking
  lastAccessAt: z.string().datetime().optional(),
  accessCount: z.number().int().default(0),

  // Audit
  createdAt: z.string().datetime(),
  createdBy: z.string().uuid(),
  updatedAt: z.string().datetime(),
});

export type AuditorAccessToken = z.infer<typeof AuditorAccessTokenSchema>;

/**
 * Archive Entry - Individual archived document/artifact
 */
export const ArchiveEntrySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),

  // Document reference
  documentId: z.string().uuid().optional(),
  documentType: z.enum(["INVOICE", "CREDIT_NOTE", "DEBIT_NOTE", "DUNNING", "EMAIL", "ATTACHMENT", "AUDIT_LOG"]),
  documentNumber: z.string().optional(),

  // Archive metadata
  archivedAt: z.string().datetime(),
  retentionUntil: z.string().datetime(), // Calculated: archivedAt + retentionYears
  fiscalYear: z.number().int(),

  // Storage
  r2Key: z.string(), // Primary storage key in R2
  r2KeyBackup: z.string().optional(), // Backup copy key
  fileSize: z.number().int(),
  mimeType: z.string(),

  // Integrity
  contentHash: z.string(), // SHA-256 hash of content
  hashAlgorithm: z.string().default("SHA-256"),
  hashVerifiedAt: z.string().datetime().optional(),
  hashVerificationStatus: z.enum(["VALID", "INVALID", "PENDING"]).default("PENDING"),

  // WORM compliance
  isImmutable: z.boolean().default(true), // Once archived, cannot be modified
  immutableSince: z.string().datetime().optional(),

  // Legal hold
  legalHold: z.boolean().default(false),
  legalHoldReason: z.string().optional(),

  // Metadata
  metadata: z.record(z.string(), z.any()).optional(),

  // Audit
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type ArchiveEntry = z.infer<typeof ArchiveEntrySchema>;

/**
 * Archive Access Log - Tracks all access to archived documents
 */
export const ArchiveAccessLogSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  archiveEntryId: z.string().uuid().optional(),

  // Who accessed
  userId: z.string().uuid().optional(),
  auditorTokenId: z.string().uuid().optional(), // If accessed via auditor token
  userEmail: z.string().email(),
  userRole: z.enum(["ADMIN", "MEMBER", "VIEWER", "ACCOUNTANT", "AUDITOR", "SYSTEM_ADMIN"]),

  // What was accessed
  action: z.enum(["VIEW", "DOWNLOAD", "EXPORT", "SEARCH", "VERIFY_INTEGRITY", "SET_LEGAL_HOLD", "REMOVE_LEGAL_HOLD"]),
  documentType: z.string().optional(),
  documentId: z.string().uuid().optional(),

  // Access context
  ipAddress: z.string(),
  userAgent: z.string().optional(),
  accessedAt: z.string().datetime(),

  // Result
  success: z.boolean(),
  failureReason: z.string().optional(),

  // Export details
  exportFormat: z.string().optional(),
  exportFileSize: z.number().int().optional(),
});

export type ArchiveAccessLog = z.infer<typeof ArchiveAccessLogSchema>;

/**
 * Integrity Check Report
 */
export const IntegrityCheckReportSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),

  // Check details
  checkType: z.enum(["SCHEDULED", "MANUAL", "ON_ACCESS"]),
  startedAt: z.string().datetime(),
  completedAt: z.string().datetime().optional(),

  // Results
  status: z.enum(["RUNNING", "PASSED", "FAILED", "PARTIAL"]),
  totalEntries: z.number().int(),
  checkedEntries: z.number().int(),
  passedEntries: z.number().int(),
  failedEntries: z.number().int(),

  // Failed entries details
  failures: z.array(z.object({
    archiveEntryId: z.string().uuid(),
    documentNumber: z.string().optional(),
    expectedHash: z.string(),
    actualHash: z.string().optional(),
    error: z.string().optional(),
  })).default([]),

  // Initiated by
  initiatedBy: z.string().uuid().optional(),

  createdAt: z.string().datetime(),
});

export type IntegrityCheckReport = z.infer<typeof IntegrityCheckReportSchema>;
